/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { Fragment } from 'react';
import {
  renderNumericValue,
  conversionParams,
  getListValueDataData,
  getInputValue, isUtrvAssuranceComplete, isUtrvAssuranceRestated,
} from '../../utils/universalTrackerValue'
import TableInputView from '../survey/form/view/TableInputView'
import { tableDataToView } from '../../utils/valueDataTable';
import { ConversionData, ValueHistory } from '../../types/universalTrackerValue';
import UniversalTracker from '../../model/UniversalTracker';
import { UniversalTrackerValuePlain } from '../../types/surveyScope';
import UniversalTrackerValue from '../../model/UniversalTrackerValue';
import { ValueDataData, ValueDataObject } from '../survey/question/questionInterfaces';
import { NotApplicableTypes } from '../../constants/status';
import { UtrValueType } from '../../types/universalTracker';

type StatusTextString = string | JSX.Element;

export class ValueHistoryText {

  static getValueText(
    h: ValueHistory,
    statusTextString: StatusTextString,
    utr: UniversalTracker,
    className = ''
  ) {
    const { value, sampleSize, valueData } = h;
    const inputValue = valueData?.input?.value ? valueData.input.value : value;
    const valueString = ValueHistoryText.getValueString(inputValue, h, valueData, utr, className)

    const sampleSizeString = typeof sampleSize === 'number' ?
      <React.Fragment>out of {ValueHistoryText.withUnits(h, sampleSize, utr)}</React.Fragment>: '';

    return <span>
      Data point {statusTextString} with a value of {valueString} {sampleSizeString}
    </span>;
  }

  static getValueString(
    value: number | undefined,
    h: ValueHistory,
    valueData: UniversalTrackerValuePlain['valueData'] | ValueDataData,
    utr: UniversalTracker,
    className?: string
  ) {
    if (value === undefined) {
      return valueData &&
        typeof valueData === 'object' &&
        'notApplicableType' in valueData &&
        valueData.notApplicableType === 'not_reported'
        ? 'N/R'
        : 'N/A';
    }
    return ValueHistoryText.withUnits(h, value, utr, className)
  }

  static withUnits(
    utrvLike: UniversalTrackerValue | UniversalTrackerValuePlain | ValueHistory,
    value: number | string | undefined,
    utr: UniversalTracker,
    className = '',
  ) {
    if (isNaN(value as number) || !utr || !utr.isNumeric()) {
      return <span className='strong text-primary'>{value}</span>;
    }

    if (utr.isPercentage()) {
      return <>{value}{utr.getSuffix()}</>
    }

    const data = conversionParams(utrvLike, utr, { value } as Partial<ConversionData>)
    return renderNumericValue(data, className)
  }

  static getValueDataText(
    typeString: string,
    universalTracker: UniversalTracker,
    h: ValueHistory,
    statusTextString: StatusTextString,
    className = '',
  ) {
    const valueData: ValueDataData | undefined = h.valueData?.data
      ? Array.isArray(h.valueData?.data)
        ? universalTracker.getValueListMultiData(h.valueData.data)
        : universalTracker.getValueListData(h.valueData.data)
      : undefined;

    if (statusTextString === 'Created' && valueData === undefined) {
      return `New data point ${statusTextString}`;
    }

    const dataString = ValueHistoryText.getDataString(valueData, h);

    return <span>{typeString} was {statusTextString} with <span className={`${className} strong`}>{dataString} </span></span>;
  }

  private static getDataString(valueData: ValueDataData | undefined, h: ValueHistory): JSX.Element | string {
    if (valueData === undefined) {
      return 'N/A';
    }
    if (h.valueData && h.valueData.notApplicableType === NotApplicableTypes.nr) {
      return 'N/R';
    }
    if (Array.isArray(valueData)) {
      return <ul>{(valueData).map((d) => <li key={d}>{d}</li>)}</ul>;
    }

    if (typeof valueData === 'object') {
      return <ul>{Object.keys(valueData).map((k) => <li>{valueData[k]}</li>)}</ul>;
    }

    return valueData;
  }

  static getTableView(
    typeString: string,
    universalTracker: UniversalTracker,
    h: ValueHistory,
    statusTextString: StatusTextString,
  ) {

    const { rows, editRowId } = tableDataToView(h.valueData);

    if (statusTextString === 'Created') {
      return `New data point ${statusTextString}`;
    }

    if (rows.length > 0) {
      return (
        <span>
          {typeString} was {statusTextString} <span className={'strong'}>with</span>
          <TableInputView
            className={'mb-4 mt-2 p-2'}
            universalTracker={universalTracker} disabled={true}
            editRowId={editRowId}
            rowData={rows}
            ignoreType= {true} />
        </span>
      )
    }
    const dataString = h.valueData && h.valueData.notApplicableType === 'not_reported' ? 'N/R' : 'N/A';
    return <span>{typeString} was {statusTextString} <span className={'strong'}>with</span> {dataString}</span>;
  }

  static getValueDataList(
    typeString: string,
    universalTracker: UniversalTracker,
    h: ValueHistory,
    statusTextString: StatusTextString,
    className = ''
  ) {
    let dataString: StatusTextString = '';

    const valueData = getListValueDataData(h) as ValueDataObject;
    const valueKeys = valueData ? Object.keys(valueData) : undefined;

    if (statusTextString === 'Created' && valueData === undefined) {
      return `New data point ${statusTextString}`;
    }

    const isNumericList = universalTracker.getValueType() === UtrValueType.NumericValueList;
    switch (true) {
      case Array.isArray(valueKeys) && valueKeys.length > 0:
        const inputValue = getInputValue(h);
        dataString = (
          <ul className={`keyValue ${isNumericList ? 'inline-rows' : ''}`}>
            {(valueKeys as string[]).map((k: string) => (
              <li key={k}>
                <span className='value-label strong'>
                  {universalTracker.getValueListData(k)}:
                </span><div className={'value-data-value'}>
                  {ValueHistoryText.withUnits(h, valueData[k], universalTracker, className)}
                </div>
              </li>
            ))}

            {isNumericList && <li key={'total'}>
              <span className='value-label strong'>Total:</span>
              <div className={'value-data-value'}>
                {ValueHistoryText.getValueString(inputValue, h, valueData, universalTracker, className)}
              </div>
            </li>}
          </ul>
        );
        break;
      case h.valueData && h.valueData.notApplicableType === NotApplicableTypes.nr:
        dataString = <span className={`${className} strong`}>'N/R'</span>;
        break;
      case valueData === undefined:
      default:
        dataString = <span className={`${className} strong`}>'N/A'</span>;
        break;
    }

    return <span>{typeString} was {statusTextString} <span className={'strong'}>with</span> {dataString}</span>;
  }

  static getTextString(
    universalTracker: UniversalTracker,
    history: ValueHistory,
    statusTextString: StatusTextString,
    className?: string
  ) {
    const valueType = history.valueType ?? universalTracker.getValueType()
    switch (valueType) {
      case 'date':
        return ValueHistoryText.getValueDataText('Date', universalTracker, history, statusTextString, className);
      case 'text':
        return ValueHistoryText.getValueDataText('Response', universalTracker, history, statusTextString, className);
      case 'valueList':
        return ValueHistoryText.getValueDataText('Option selection', universalTracker, history, statusTextString, className);
      case 'valueListMulti':
        return ValueHistoryText.getValueDataText('Option(s) selection', universalTracker, history, statusTextString, className);
      case 'textValueList':
      case 'numericValueList':
        return ValueHistoryText.getValueDataList('Option(s) selection', universalTracker, history, statusTextString, className);
      case 'table':
        return ValueHistoryText.getTableView('Option(s) selection', universalTracker, history, statusTextString)
      case 'number':
      case 'percentage':
      case 'sample':
      default:
        return ValueHistoryText.getValueText(history, statusTextString, universalTracker, className);
    }
  }

  static getHistoryText(
    className: string,
    statusText: string,
    history: ValueHistory,
    username: string,
    universalTracker: UniversalTracker
  ) {
    if (statusText === 'Verified' && isUtrvAssuranceComplete(history.assuranceStatus)) {
      statusText = 'Assured';
    }

    if (isUtrvAssuranceRestated(history.assuranceStatus)) {
      statusText = 'Restated';
    }

    const isImported = history.valueData?.isImported === true;
    const statusTextString = <span className={className}><strong>{statusText}</strong>{isImported ? ' (via Import File)' : ''} </span>;
    let textString;
    if (['Created'].includes(statusText)) {
      return <Fragment>{statusTextString}</Fragment>;
    } else if (['Rejected', 'Verified', 'Assured'].includes(statusText)) {
      textString = <Fragment>Data {statusTextString}</Fragment>;
    } else {
      textString = ValueHistoryText.getTextString(universalTracker, history, statusTextString, className);
    }

    return <Fragment>
      {textString} by <strong>{username}</strong>
    </Fragment>;
  }

  static getCompositeHistoryText(
    className: string | undefined,
    statusText: string,
    history: ValueHistory,
    utr: UniversalTracker
  ) {
    switch (statusText) {
      case 'Rejected':
        return 'There is not enough information to calculate data point';
      case 'Updated':
        return 'Calculation reset due to changes in underlying data points';
      default:
      case 'Verified':
        return ValueHistoryText.getValueText(history, 'successfully calculated', utr, className);
    }
  }

  static getFrameworkUtrvHistoryText(
    statusText: string,
    username: string,
    className?: string
  ) {
    return <Fragment>
      Universal Tracker has been
      <span className={className}> <strong>{statusText}</strong></span> by <strong>{username}</strong>
    </Fragment>;
  }
}
